import { Link } from "react-router-dom"
import {
  Users,
  Network,
  Globe,
  Handshake,
  Layers,
  BadgeCheck,
  Building2,
  Rocket,
  LineChart,
  Cpu,
  Calendar,
  DollarSign,
  ArrowRight
} from "lucide-react"
import { useLang } from "../context/LangContext"

const T = {
  en: {
    hero: "Partner Communities",
    heroSub: "uCloud360 grows through strategic alliances with entrepreneurial communities, chambers of commerce, innovation hubs, and business networks across the United States. Together, we build stronger, smarter, and more scalable ecosystems.",
    becomePartner: "Become a Partner",
    whyTitle: "Why Strategic Alliances Matter",
    whyBody1: "Growth does not happen in isolation. Entrepreneurs thrive when surrounded by the right knowledge, tools, and people. Strategic alliances allow uCloud360 and its partners to expand reach, multiply expertise, and create real impact for their communities.",
    whyBody2: "Our allied communities are not logos on a website — they are active contributors to a living ecosystem designed to support founders at every stage of their journey.",
    whyPoints: ["Expanded access to entrepreneurs", "Technology without development costs", "Shared growth and visibility", "National-level reach"],
    typesTitle: "Types of Allied Communities",
    types: [
      { name: "Entrepreneur Communities", desc: "Spaces where founders exchange ideas, experiences, and support to grow together." },
      { name: "Chambers of Commerce", desc: "Organizations that provide market access, visibility, and business guidance." },
      { name: "Innovation Hubs", desc: "Incubators, accelerators, and coworking spaces supporting startup growth." },
      { name: "Business Networks", desc: "Professional communities focused on collaboration and strategic partnerships." },
    ],
    levelsTitle: "Partner Levels & Benefits",
    levels: [
      { name: "Community Partner", desc: "Entry-level alliances designed to expand reach and collaboration.", bullets: ["Official partner profile", "Free access for members", "Cross-promotion opportunities"] },
      { name: "Strategic Partner", desc: "Deeper collaboration with content, events, and tools.", bullets: ["Featured placement", "Co-hosted events & webinars", "Early access to new tools"] },
      { name: "Ecosystem Partner", desc: "Long-term, high-impact partnerships shaping the future ecosystem.", bullets: ["Co-branded initiatives", "Revenue sharing programs", "Influence on product roadmap"] },
    ],
    howTitle: "How We Activate Partnerships",
    howBody: "We believe partnerships should generate real outcomes. Our strategy focuses on activation, not passive affiliation.",
    howPoints: ["Joint events & webinars", "Cross-community networking", "Shared monetization opportunities"],
    closeTitle: "Strong ecosystems are built together",
    closeBody: "If you lead a community that empowers entrepreneurs, we'd love to explore a partnership with you.",
    applyCta: "Apply as a Partner",
  },
  es: {
    hero: "Comunidades Aliadas",
    heroSub: "uCloud360 crece a través de alianzas estratégicas con comunidades emprendedoras, cámaras de comercio, hubs de innovación y redes empresariales en todo los Estados Unidos. Juntos construimos ecosistemas más fuertes y escalables.",
    becomePartner: "Ser Aliado",
    whyTitle: "Por Qué Importan las Alianzas Estratégicas",
    whyBody1: "El crecimiento no ocurre en aislamiento. Los emprendedores prosperan cuando están rodeados del conocimiento, herramientas y personas correctas. Las alianzas estratégicas permiten a uCloud360 y sus socios expandir alcance, multiplicar expertise y crear impacto real.",
    whyBody2: "Nuestras comunidades aliadas no son logos en un sitio web — son colaboradores activos de un ecosistema vivo diseñado para apoyar a los fundadores en cada etapa de su camino.",
    whyPoints: ["Acceso ampliado a emprendedores", "Tecnología sin costos de desarrollo", "Crecimiento y visibilidad compartidos", "Alcance a nivel nacional"],
    typesTitle: "Tipos de Comunidades Aliadas",
    types: [
      { name: "Comunidades Emprendedoras", desc: "Espacios donde fundadores intercambian ideas, experiencias y apoyo para crecer juntos." },
      { name: "Cámaras de Comercio", desc: "Organizaciones que brindan acceso al mercado, visibilidad y orientación empresarial." },
      { name: "Hubs de Innovación", desc: "Incubadoras, aceleradoras y coworkings que apoyan el crecimiento de startups." },
      { name: "Redes Empresariales", desc: "Comunidades profesionales enfocadas en colaboración y alianzas estratégicas." },
    ],
    levelsTitle: "Niveles de Alianza y Beneficios",
    levels: [
      { name: "Aliado Comunitario", desc: "Alianzas de nivel inicial diseñadas para ampliar alcance y colaboración.", bullets: ["Perfil oficial de aliado", "Acceso gratuito para miembros", "Oportunidades de promoción cruzada"] },
      { name: "Aliado Estratégico", desc: "Colaboración más profunda con contenido, eventos y herramientas.", bullets: ["Posicionamiento destacado", "Eventos y webinars co-organizados", "Acceso anticipado a nuevas herramientas"] },
      { name: "Aliado Ecosistema", desc: "Alianzas de alto impacto y largo plazo que dan forma al ecosistema futuro.", bullets: ["Iniciativas de marca compartida", "Programas de revenue sharing", "Influencia en el roadmap del producto"] },
    ],
    howTitle: "Cómo Activamos las Alianzas",
    howBody: "Creemos que las alianzas deben generar resultados reales. Nuestra estrategia se centra en la activación, no en la afiliación pasiva.",
    howPoints: ["Eventos y webinars conjuntos", "Networking entre comunidades", "Oportunidades de monetización compartida"],
    closeTitle: "Los ecosistemas fuertes se construyen juntos",
    closeBody: "Si lideras una comunidad que empodera emprendedores, nos encantaría explorar una alianza contigo.",
    applyCta: "Aplicar como Aliado",
  },
}

export default function AlliancesPage() {
  const { lang } = useLang()
  const t = T[lang]
  const muted = "text-gray-600 dark:text-gray-400"
  const card = "bg-white dark:bg-neutral-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8"
  const primaryBtn = "inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#2563EB] text-white hover:bg-[#1d4ed8] transition font-medium"


  return (
    <div className="bg-white dark:bg-black text-black dark:text-white">

      {/* HERO */}
      <section className="bg-[#F8FAFF] dark:bg-neutral-900/40">
      <div className="max-w-6xl mx-auto px-6 py-24 text-center">

        <h1 className="text-4xl md:text-5xl font-semibold flex justify-center gap-3 items-center">
          <Handshake className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          {t.hero}
        </h1>
        <p className={`mt-6 max-w-3xl mx-auto text-lg ${muted}`}>{t.heroSub}</p>
        <Link to="/contact" className={`inline-flex items-center gap-2 mt-10 ${primaryBtn}`}>
          {t.becomePartner} <ArrowRight size={16} />
        </Link>
      </div>
      </section>

      {/* WHY ALLIANCES */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12">
        <div>
          <h2 className="text-3xl font-semibold flex items-center gap-2">
            <Network className="h-6 w-6 text-[#2563EB]" />
            {t.whyTitle}
          </h2>
          <p className={`mt-4 ${muted}`}>{t.whyBody1}</p>
          <p className={`mt-4 ${muted}`}>{t.whyBody2}</p>
        </div>
        <div className={`${card} flex flex-col gap-4`}>
          {[<Users/>, <Cpu/>, <LineChart/>, <Globe/>].map((Icon, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-gray-600 dark:text-gray-400">{Icon}</span>
              <span className={`text-sm ${muted}`}>{t.whyPoints[i]}</span>
            </div>
          ))}
        </div>
      </section>

      {/* TYPES */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-semibold mb-12">{t.typesTitle}</h2>
        <div className="grid md:grid-cols-4 gap-6">
          {[Users, Building2, Rocket, Layers].map((Icon, i) => (
            <div key={i} className={card}>
              <Icon className="text-gray-700 dark:text-gray-300" />
              <h3 className="mt-4 font-semibold">{t.types[i].name}</h3>
              <p className={`mt-2 text-sm ${muted}`}>{t.types[i].desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LEVELS */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-semibold mb-12">{t.levelsTitle}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[BadgeCheck, Network, Handshake].map((Icon, i) => (
            <div key={i} className={card}>
              <Icon className="text-gray-700 dark:text-gray-300" />
              <h3 className="mt-4 font-semibold">{t.levels[i].name}</h3>
              <p className={`mt-2 text-sm ${muted}`}>{t.levels[i].desc}</p>
              <ul className="mt-4 space-y-2 text-sm">
                {t.levels[i].bullets.map((b) => (
                  <li key={b} className={muted}>• {b}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* STRATEGY */}
      <section className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12">
        <div>
          <h2 className="text-3xl font-semibold">{t.howTitle}</h2>
          <p className={`mt-4 ${muted}`}>{t.howBody}</p>
        </div>
        <div className={`${card} space-y-4`}>
          {[Calendar, Users, DollarSign].map((Icon, i) => (
            <div key={i} className="flex items-center gap-3">
              <Icon className="text-gray-600 dark:text-gray-400" />
              <span className={`text-sm ${muted}`}>{t.howPoints[i]}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 py-24 text-center">
        <h2 className="text-3xl font-semibold">{t.closeTitle}</h2>
        <p className={`mt-4 ${muted}`}>{t.closeBody}</p>
        <Link to="/contact" className={`inline-flex items-center gap-2 mt-8 ${primaryBtn}`}>
          {t.applyCta} <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  )
}
